<script>
import { computed, defineComponent, ref } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { usePointTracing } from '@/src/composables/usePointTracing';

import { useSegmentationDialog } from '@/src/composables/useSegmentationDialog';
import {
  ContourLabel,
  ContourLabelToIcon,
} from '@/src/store/contours/contour-definitions';
// import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export default defineComponent({
  name: 'PieMenu',
  props: {
    viewId: {
      type: String,
      required: true,
    },
    currentImageUID: {
      type: String,
      required: true,
    },
    radius: {
      type: Number,
      default: 85,
    },
    corner: {
      type: Boolean,
      default: false,
    },
    cornerPosition: {
      type: String,
      default: 'top-left',
    },
    iconSize: {
      type: Number,
      default: 20,
    },
    innerRadiusRatio: {
      type: Number,
      default: 0.6,
    },
    defaultIcon: {
      type: String,
      default:
        'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'white\'%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'6\'/%3E%3C/svg%3E',
    },
    clickX: {
      type: Number,
      default: null,
    },
    clickY: {
      type: Number,
      default: null,
    },
  },
  emits: ['select', 'close'],
  setup(props, { emit }) {
    const viewportActions = useViewportActions();
    // const backendSegmentation = useBackendSegmentation();
    const viewportStateStore = useViewportStateStore();
    const segDialog = useSegmentationDialog();

    // Wrap the actions to include the emit('close')
    function wrapWithClose(action) {
      return (...args) => {
        action(...args);
        emit('close');
      };
    }

    const currentStudyStore = useCurrentStudyStore();
    const currentImageIndices = computed(() =>
      viewportStateStore.getSelectedImageIndices(props.viewId),
    );
    const selectedContour = computed(() =>
      viewportStateStore.getSelectedContour(props.viewId),
    );

    function isBreathholdAvailable() {
      try {
        // If prop.viewerId is not ContoursLeft || ContoursRight, just return false
        if (
          !props.viewId.includes('ContoursLeft')
          && !props.viewId.includes('ContoursRight')
        ) {
          return false;
        }
        const studyObj = viewportActions.getStudyObjectByViewerId(props.viewId);
        if (!studyObj) return false;
        const breathholdState = studyObj.getBreathhold();
        return breathholdState !== undefined && breathholdState !== null;
      }
      catch {
        return false;
      }
    }

    // Define contour types for reuse
    const contourTypes = [
      { label: 'SAX Endo', contourLabel: ContourLabel.SaxLvEndo },
      { label: 'SAX Epi', contourLabel: ContourLabel.SaxLvEpi },
      { label: 'SAX Open Epi', contourLabel: ContourLabel.SaxLvOpenEpi },
      { label: 'RV Insertion Point', contourLabel: ContourLabel.RvInsertion },
      { label: 'SAX RV Endo', contourLabel: ContourLabel.SaxRvEndo },
      { label: 'SAX RV Papillary', contourLabel: ContourLabel.SaxRvPapillary },
      { label: 'SAX Papillary', contourLabel: ContourLabel.SaxLvPapillary },
      { label: 'SAX Exclusion', contourLabel: ContourLabel.SaxLvExclusion },
      { label: 'LAX Endo', contourLabel: ContourLabel.LaxLvEndo },
      { label: 'LAX Epi', contourLabel: ContourLabel.LaxLvEpi },
      { label: 'LAX Range', contourLabel: ContourLabel.LaxLvRange },
      { label: 'Flow Roi 1', contourLabel: ContourLabel.FlowRoi1 },
      { label: 'Flow Roi 2', contourLabel: ContourLabel.FlowRoi2 },
      {
        label: 'Flow Static Tissue Roi',
        contourLabel: ContourLabel.FlowStaticTissueRoi,
      },
      { label: 'Freehand', contourLabel: ContourLabel.GeneralPolygon },
      { label: 'Distance', contourLabel: ContourLabel.GeneralLine },
      { label: 'Mask Measurement', contourLabel: ContourLabel.MaskMeasurement },
      { label: 'Annotation Arrow', contourLabel: ContourLabel.AnnotationArrow },
      { label: 'Text', contourLabel: ContourLabel.AnnotationText },
    ];

    function hasUserSelectedContour() {
      // Check if selected contour has label and id (and that they are not null)
      return (
        selectedContour.value
        && selectedContour.value.label
        && selectedContour.value.id
      );
    }

    // Helper function to create contour action items
    function createContourActionItems(action) {
      const currentContours = viewportActions.getImageDrawingContours(
        props.currentImageUID,
      );
      // Check the selectedContour.value?.label
      console.log('Selected contour label:', selectedContour.value);
      if (!currentContours) {
        return null;
      }
      const { seriesIdx, imageIdx, sliceIdx } = currentImageIndices.value;

      if (action === 'Forward') {
        return [
          {
            label: 'Forward Selected Across Series',
            icon: '/icons/contextmenu/across_series_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              // Get Series Image UIDs
              const imageUIDs = currentStudyStore.getSeriesImageUIDs(seriesIdx);
              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Forward Selected Across Phase',
            icon: '/icons/contextmenu/across_phase_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              // First get all image UIDs across the phases:
              const imageUIDs = currentStudyStore.getPhaseImageUIDs(
                seriesIdx,
                imageIdx,
              );
              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Forward Selected Across Slice',
            icon: '/icons/contextmenu/across_slice_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );

              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },

          {
            label: 'Forward All Across Slice',
            icon: '/icons/contextmenu/across_slice_all_draft.svg',
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );

              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
              );
            }),
          },
          {
            label: 'Forward All Across Phase',
            icon: '/icons/contextmenu/across_phase_all_draft.svg',
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getPhaseImageUIDs(
                seriesIdx,
                imageIdx,
              );

              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
              );
            }),
          },
          {
            label: 'Forward All Across Series',
            icon: '/icons/contextmenu/across_series_all_draft.svg',
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSeriesImageUIDs(seriesIdx);

              viewportActions.forwardContoursAcrossUIDs(
                props.currentImageUID,
                imageUIDs,
              );
            }),
          },
        ];
      }
      if (action === 'Delete') {
        return [
          {
            label: 'Delete Selected Across Series',
            icon: '/icons/contextmenu/across_series_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSeriesImageUIDs(seriesIdx);
              viewportActions.handleDeleteAcrossImages(
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Delete Selected Across Phase',
            icon: '/icons/contextmenu/across_phase_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getPhaseImageUIDs(
                seriesIdx,
                imageIdx,
              );
              viewportActions.handleDeleteAcrossImages(
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Delete Selected Across Slice',
            icon: '/icons/contextmenu/across_slice_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );
              viewportActions.handleDeleteAcrossImages(
                imageUIDs,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Delete All Across Slice',
            icon: '/icons/contextmenu/across_slice_all_draft.svg',
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );
              viewportActions.handleDeleteAcrossImages(imageUIDs);
            }),
          },
          {
            label: 'Delete All in Phase',
            icon: '/icons/contextmenu/current_phase_all_draft.svg',
            onClick: wrapWithClose(() => {
              viewportActions.handleDeleteAll(props.currentImageUID);
            }),
          },

          {
            label: 'Delete Selected',
            icon: '/icons/contextmenu/current_phase_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              console.log('Delete Selected');
              viewportActions.handleDelete(props.viewId);
            }),
          },

          {
            label: 'Delete All Across Series',
            icon: '/icons/contextmenu/across_series_all_draft.svg',
            onClick: wrapWithClose(() => {
              const imageUIDs = currentStudyStore.getSeriesImageUIDs(seriesIdx);
              viewportActions.handleDeleteAcrossImages(imageUIDs);
            }),
          },
        ];
      }
      if (action === 'Copy') {
        return [
          {
            label: 'Copy Selected',
            icon: '/icons/contextmenu/current_phase_select_draft.svg',
            disabled: !hasUserSelectedContour(),
            onClick: wrapWithClose(() => {
              viewportActions.copyImageDrawingContours(
                props.currentImageUID,
                selectedContour.value?.label,
              );
            }),
          },
          {
            label: 'Copy All',
            icon: '/icons/contextmenu/current_phase_all_draft.svg',
            onClick: wrapWithClose(() => {
              viewportActions.copyImageDrawingContours(props.currentImageUID);
            }),
          },
        ];
      }

      return null;
    }

    const itemConfig = [
      {
        label: 'Undo',
        icon: '/icons/contextmenu/undo_icon_draft.svg',
        onClick: wrapWithClose(viewportActions.handleUndo),
      },
      {
        label: 'Redo',
        icon: '/icons/contextmenu/redo_icon_draft.svg',
        onClick: wrapWithClose(viewportActions.handleRedo),
      },
      {
        label: 'Delete',
        icon: '/icons/contextmenu/delete_icon_draft.svg',
        subItems: createContourActionItems('Delete'),
      },
      {
        label: 'Copy',
        icon: '/icons/contextmenu/copy_icon_draft.svg',
        subItems: createContourActionItems('Copy'),
      },
      {
        label: 'Paste',
        icon: '/icons/contextmenu/paste_icon_draft.svg',
        onClick: wrapWithClose(() =>
          viewportActions.pasteContours(props.currentImageUID),
        ),
      },
      {
        label: 'Forward Contours',
        icon: '/icons/contextmenu/forward_icon_draft.svg',
        subItems: createContourActionItems('Forward'),
      },
      {
        label: 'Contour Processing',
        icon: '/icons/contextmenu/contour_post_icon_draft.svg',
        subItems: [
          {
            label: 'Trace Insertion Point',
            icon: '/icons/contextmenu/optical_forward_draft.svg',
            onClick: wrapWithClose(() =>
              usePointTracing(props.viewId, props.currentImageUID),
            ),
          },
          {
            label: 'Smooth Contours',
            icon: '/icons/contextmenu/round_contour_icon_draft.svg',
            onClick: wrapWithClose(() =>
              console.log('Smooth Contours clicked'),
            ),
          },
        ],
      },
      {
        label: 'Draw Contours',
        icon: '/icons/contextmenu/drawing_icon_draft.svg',
        subItems: contourTypes
          .filter(({ contourLabel }) =>
            viewportStateStore.isContourLabelAllowed(props.viewId, contourLabel),
          )
          .map(({ label, contourLabel }) => ({
            label,
            icon: ContourLabelToIcon[contourLabel],
            onClick: wrapWithClose(() =>
              viewportActions.setDrawingContour(props.viewId, contourLabel),
            ),
          })),
      },
      {
        label: 'Image Interpolation',
        icon: '/icons/image-interaction/interp_menu_icon.svg',
        subItems: [
          {
            label: 'Nearest',
            icon: '/icons/image-interaction/interp_nearest_icon.svg',
            onClick: wrapWithClose(() =>
              viewportActions.setImageInterpolationMode(props.viewId, 'nearest'),
            ),
          },
          {
            label: 'Linear',
            icon: '/icons/image-interaction/interp_linear_icon.svg',
            onClick: wrapWithClose(() =>
              viewportActions.setImageInterpolationMode(props.viewId, 'linear'),
            ),
          },
          {
            label: 'Cubic',
            icon: '/icons/image-interaction/interp_cubic_icon.svg',
            onClick: wrapWithClose(() =>
              viewportActions.setImageInterpolationMode(props.viewId, 'cubic'),
            ),
          },
        ],
      },
      {
        label: 'DICOM Dump',
        icon: '/icons/contextmenu/dicomdump_icon_draft.svg',
        onClick: wrapWithClose(() =>
          viewportActions.showDicomDump(props.viewId),
        ),
      },
      {
        label: 'Toggle PixelGrid',
        icon: '/icons/contextmenu/grid_icon_draft.svg',
        onClick: wrapWithClose(() => viewportActions.toggleGrid(props.viewId)),
      },
      {
        label: 'Segmentation',
        icon: '/icons/contextmenu/segment_icon_draft.svg',
        subItems: [
          {
            label: 'Segment Current Image',
            icon: '/icons/contextmenu/current_phase_all_draft.svg',
            onClick: wrapWithClose(
              () =>
                segDialog.trigger([
                  {
                    viewerID: props.viewId,
                    imageUIDs: [props.currentImageUID],
                  },
                ]),
              // backendSegmentation.segmentandAdd({
              //   include: [props.currentImageUID],
              // })
            ),
          },
          {
            label: 'Segment All Across Slice',
            icon: '/icons/contextmenu/across_slice_all_draft.svg',
            onClick: wrapWithClose(() => {
              const { seriesIdx, sliceIdx } = currentImageIndices.value;
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );

              segDialog.trigger([
                {
                  viewerID: props.viewId,
                  imageUIDs,
                },
              ]);
              // backendSegmentation.segmentandAdd({
              //   include: imageUIDs,
              // });
            }),
          },
          {
            label: 'Segment Breath-Hold Images',
            icon: '/icons/breathhold-icon-draft.svg',
            disabled: !isBreathholdAvailable(),
            onClick: wrapWithClose(() => {
              const { seriesIdx, sliceIdx } = currentImageIndices.value;
              const imageUIDs = currentStudyStore.getSliceImageUIDs(
                seriesIdx,
                sliceIdx,
              );
              const studyObj = viewportActions.getStudyObjectByViewerId(
                props.viewId,
              );
              const breathholdState = studyObj.getBreathhold();

              if (!breathholdState) {
                throw new Error(
                  `No breathhold state found for viewerId: ${props.viewId}`,
                );
              }
              const start = breathholdState.getStart();
              const end = breathholdState.getEnd();
              const croppedImageUIDs = imageUIDs.slice(start, end + 1);

              segDialog.trigger([
                {
                  viewerID: props.viewId,
                  imageUIDs: croppedImageUIDs,
                },
              ]);
              // backendSegmentation.segmentandAdd({
              //   include: croppedImageUIDs,
              // });
            }),
          },
        ],
      },
    ];

    const menuHistory = ref([itemConfig]);
    const currentLevel = ref(0);
    const closeButtonRadius = computed(() => props.radius * 0.15);
    const innerRadius = computed(() => props.radius * props.innerRadiusRatio);

    const currentItems = computed(() => menuHistory.value[currentLevel.value]);

    const totalAngle = computed(() => {
      const angle = props.corner ? Math.PI / 2 : 2 * Math.PI;
      console.log('Total angle computed:', angle);
      return angle;
    });

    const startAngleOffset = computed(() => {
      if (props.corner) {
        switch (props.cornerPosition) {
          case 'top-left':
            return 0;
          case 'top-right':
            return Math.PI / 2;
          case 'bottom-right':
            return Math.PI;
          case 'bottom-left':
            return (3 * Math.PI) / 2;
          default:
            return 0;
        }
      }
      return 0;
    });

    const center = computed(() => {
      return { x: props.radius, y: props.radius };
    });

    const svgSize = computed(() => {
      const size = props.corner ? props.radius : props.radius * 2;
      return size;
    });

    function goToSubMenu(items) {
      menuHistory.value.push(items);
      currentLevel.value++;
    }

    function goBack() {
      if (currentLevel.value > 0) {
        currentLevel.value--;
        menuHistory.value.pop();
      }
    }

    function polarToCartesian(centerX, centerY, radius, angleInRadians) {
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    }

    function describeArc(startAngle, endAngle) {
      const outerR = props.radius;
      const innerR = innerRadius.value;
      const outerStart = polarToCartesian(
        center.value.x,
        center.value.y,
        outerR,
        startAngle,
      );
      const outerEnd = polarToCartesian(
        center.value.x,
        center.value.y,
        outerR,
        endAngle,
      );
      const innerStart = polarToCartesian(
        center.value.x,
        center.value.y,
        innerR,
        startAngle,
      );
      const innerEnd = polarToCartesian(
        center.value.x,
        center.value.y,
        innerR,
        endAngle,
      );

      const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';

      return [
        'M',
        outerStart.x,
        outerStart.y,
        'A',
        outerR,
        outerR,
        0,
        largeArcFlag,
        1,
        outerEnd.x,
        outerEnd.y,
        'L',
        innerEnd.x,
        innerEnd.y,
        'A',
        innerR,
        innerR,
        0,
        largeArcFlag,
        0,
        innerStart.x,
        innerStart.y,
        'Z',
      ].join(' ');
    }

    function getPath(index) {
      const n = currentItems.value.length;
      const sliceAngle = totalAngle.value / n;
      const startAngle = startAngleOffset.value + index * sliceAngle;
      const endAngle = startAngle + sliceAngle;
      return describeArc(startAngle, endAngle);
    }

    function getIconPosition(index) {
      const n = currentItems.value.length;
      const sliceAngle = totalAngle.value / n;
      const angle
        = startAngleOffset.value + index * sliceAngle + sliceAngle / 2;
      const midRadius = (props.radius + innerRadius.value) / 2;
      return polarToCartesian(center.value.x, center.value.y, midRadius, angle);
    }

    function handleClick(item) {
      if (item.subItems) {
        goToSubMenu(item.subItems);
      }
      else if (item.onClick) {
        item.onClick();
      }
    }

    const menuStyle = computed(() => ({
      position: 'absolute',
      left: `${props.clickX}px`,
      top: `${props.clickY}px`,
      transform: 'translate(-50%, -50%)',
    }));

    return {
      svgSize,
      getPath,
      getIconPosition,
      handleClick,
      center,
      closeButtonRadius,
      innerRadius,
      currentItems,
      currentLevel,
      goBack,
      menuStyle,
      currentStudyStore,
    };
  },
});
</script>

<template>
  <svg
    :width="svgSize"
    :height="svgSize"
    class="pie-menu"
    :style="menuStyle"
    @mousedown.stop.prevent
  >
    <template v-for="(item, index) in currentItems" :key="index">
      <!-- Render each ring segment with tooltip -->
      <g class="segment-group" :class="{ 'disabled-clickable': item.disabled }">
        <path
          :d="getPath(index)"
          class="ring-segment"
          @click.stop.prevent="handleClick(item)"
          @keydown.enter.stop.prevent="handleClick(item)"
        >
          <title>{{ item.label }}</title>
        </path>
        <!-- Always render icon, use default if not provided -->
        <image
          :href="item.icon || defaultIcon"
          :x="getIconPosition(index).x - iconSize / 2"
          :y="getIconPosition(index).y - iconSize / 2"
          :width="iconSize"
          :height="iconSize"
          :class="{ 'disabled-item': item.disabled }"
        >
          <title>{{ item.label }}</title>
        </image>
      </g>
    </template>
    <!-- Back button (only show when in sub-menu) -->
    <g
      v-if="currentLevel > 0"
      class="back-button"
      @click.stop.prevent="goBack"
      @keydown.enter.stop.prevent="goBack"
    >
      <circle
        :cx="center.x"
        :cy="center.y"
        :r="innerRadius"
        fill="rgba(255,255,255,0.1)"
        class="close-circle"
      />
      <!--         <circle
          :cx="center.x"
          :cy="center.y"
          :r="closeButtonRadius"
          fill="rgba(255,255,255,0.2)"
          class="back-circle">
          <title>Back</title>
        </circle> -->
      <path
        :d="`M ${center.x + closeButtonRadius / 2} ${center.y} L ${
          center.x - closeButtonRadius / 2
        } ${center.y} L ${center.x} ${center.y - closeButtonRadius / 2}`"
        fill="none"
        stroke="#fff"
        stroke-width="2"
      />
    </g>
    <!-- Close button (only show in root menu) -->
    <g
      v-else
      class="close-button"
      @click.stop.prevent="$emit('close')"
      @keydown.enter.stop.prevent="$emit('close')"
    >
      <circle
        :cx="center.x"
        :cy="center.y"
        :r="innerRadius"
        fill="rgba(255,255,255,0.1)"
        class="close-circle"
      >
        <title>Close Menu</title>
      </circle>
      <line
        :x1="center.x - closeButtonRadius / 2"
        :y1="center.y - closeButtonRadius / 2"
        :x2="center.x + closeButtonRadius / 2"
        :y2="center.y + closeButtonRadius / 2"
        stroke="#fff"
        stroke-opacity="0.2"
        stroke-width="2"
      />
      <line
        :x1="center.x - closeButtonRadius / 2"
        :y1="center.y + closeButtonRadius / 2"
        :x2="center.x + closeButtonRadius / 2"
        :y2="center.y - closeButtonRadius / 2"
        stroke="#fff"
        stroke-opacity="0.2"
        stroke-width="2"
      />
    </g>
    <!-- Add an invisible background to capture all events -->
    <!-- <rect x="0" y="0" :width="svgSize" :height="svgSize" fill="transparent" @click.stop.prevent /> -->
  </svg>
</template>

<style scoped>
.pie-menu {
  cursor: pointer;
  position: relative;
  z-index: 1000;
  pointer-events: all;
  /* Force capturing all pointer events */
  user-select: none;
  /* Prevent text selection */
}

.ring-segment {
  stroke: var(--md-border);
  stroke-width: 1;
  transition: fill 0.3s;
  fill: var(--glass-bg-primary);
  /* Default light grey color */
}

.segment-group:hover .ring-segment {
  filter: brightness(1.2);
}

.segment-group image {
  transition: transform 0.2s ease;
  pointer-events: none;
  transform-origin: center;
}

.segment-group:hover image {
  transform: scale(1.02);
}

.close-button,
.back-button {
  cursor: pointer;
}

.close-circle,
.back-circle {
  transition: fill 0.3s;
}

.back-button:hover .back-circle {
  fill: rgba(255, 255, 255, 0.3);
}

.close-button image,
.back-button image {
  pointer-events: none;
  transition: transform 0.2s ease;
  transform-origin: center;
}

.close-button:hover image,
.back-button:hover image {
  transform: scale(1.02);
}

.disabled-item {
  opacity: 0.4;
  pointer-events: none;
}

.disabled-clickable {
  pointer-events: none;
  cursor: default;
}
</style>
